import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Stack, Card, CardContent, CardHeader, Typography, Button } from "@altimateai/lego";
import KnowledgeCatalogCard from "./KnowledgeCatalogCard";
import { useQuery } from '@tanstack/react-query';
import { getKnowledgeBaseSources } from "@/helpers/apis";
import { IconUpload, IconFileTypePdf, IconFileWord, IconMarkdown, IconFileText, IconSparkles } from "@tabler/icons-react";

interface KnowledgeBaseSourcesProps {
  searchQuery?: string;
}

const KnowledgeBaseSources = ({ searchQuery = "" }: KnowledgeBaseSourcesProps) => {
  const navigate = useNavigate();
  const {
    data: knowledgeSources,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["knowledgeSources"],
    queryFn: () => getKnowledgeBaseSources(),
  });

  const filteredSources = useMemo(() => {
    if (!knowledgeSources) return [];
    if (!searchQuery) return knowledgeSources;

    return knowledgeSources.filter(
      source =>
        source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        source.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [knowledgeSources, searchQuery]);

  return (
    <Card>
      <CardHeader>
        <Typography variant="h4">My Knowledge Documents</Typography>
      </CardHeader>
      <CardContent>
        <Stack direction="column">
          {isLoading ? (
            <Spinner />
          ) : isError ? (
            <Typography variant="body" className="al-text-red-600">
              Failed to load knowledge sources.{" "}
              {error instanceof Error ? error.message : "Please try again later."}
            </Typography>
          ) : (
            <Stack direction="row" className="al-gap-4 !al-flex-wrap">
              {filteredSources?.length ? (
                filteredSources?.map(source => (
                  <KnowledgeCatalogCard key={source.id} item={source} />
                ))
              ) : searchQuery ? (
                <Typography variant="body">
                  No knowledge sources match your search.
                </Typography>
              ) : (
                <div className="al-w-full al-py-8">
                  <Stack direction="column" align="center" gap="lg">
                    <div className="al-rounded-full al-bg-blue-100 al-p-4">
                      <IconSparkles className="al-w-8 al-h-8 al-text-blue-600" />
                    </div>
                    <Stack direction="column" align="center" gap="sm">
                      <Typography variant="h4" weight="semibold" className="al-text-gray-900">
                        Upload your first document
                      </Typography>
                      <Typography variant="body" className="al-text-gray-600 al-text-center al-max-w-md">
                        Upload documentation and query it using AI in your IDE via the DataMates IDE Extension.
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap="md" className="al-flex-wrap al-justify-center">
                      <Stack direction="row" align="center" gap="xs" className="al-text-gray-500">
                        <IconFileTypePdf className="al-w-4 al-h-4" />
                        <Typography variant="body" size="sm">PDF</Typography>
                      </Stack>
                      <Stack direction="row" align="center" gap="xs" className="al-text-gray-500">
                        <IconFileWord className="al-w-4 al-h-4" />
                        <Typography variant="body" size="sm">Word</Typography>
                      </Stack>
                      <Stack direction="row" align="center" gap="xs" className="al-text-gray-500">
                        <IconMarkdown className="al-w-4 al-h-4" />
                        <Typography variant="body" size="sm">Markdown</Typography>
                      </Stack>
                      <Stack direction="row" align="center" gap="xs" className="al-text-gray-500">
                        <IconFileText className="al-w-4 al-h-4" />
                        <Typography variant="body" size="sm">Text</Typography>
                      </Stack>
                    </Stack>
                    <Button onClick={() => navigate("/knowledge-hub/new")}>
                      <IconUpload className="al-w-4 al-h-4 al-mr-2" />
                      Upload Document
                    </Button>
                  </Stack>
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default KnowledgeBaseSources;
